import React, { useEffect, useState } from 'react';

import CartSummaryWrapper from '~/components/cart/cart-summary/cart-summary-wrapper';
import CartItemList from '~/components/cart/cart-summary/cart-item-list';
import CouponForm from '~/components/cart/cart-summary/coupon-form';
import CouponLine from '~/components/cart/cart-summary/coupon-line';
import StoreCreditLine from '~/components/cart/cart-summary/store-credit-line';
import ShippingLine from '~/components/cart/cart-summary/shipping-line';
import SubtotalLine from '~/components/cart/cart-summary/subtotal-line';
import TaxLine from '~/components/cart/cart-summary/tax-line';
import TotalLine from '~/components/cart/cart-summary/total-line';
import { cartPurchaseOrderSelector } from '~/features/cart/cart';
import { useCartMutations } from '~/features/cart/cart-actions';
import { deleteSavedUrlCoupon, parseCouponError } from '~/features/cart/cart';

import styles from '~/components/cart/cart-summary/cart-summary.module.scss';

const AuthFailModalCartSummary = ({
  cart,
  showCouponLine = true,
  coupon,
  onCouponAdd = () => {},
  onCouponRemove = () => {},
}) => {
  const { addCoupon } = useCartMutations();

  const [couponError, setCouponError] = useState(null);

  // If the user landed on site with a coupon in the URL try to apply it
  useEffect(() => {
    if (coupon && showCouponLine && !cart?.coupon) {
      addCoupon(cart.uuid, coupon)
        .then(async (response) => {
          onCouponAdd(response);
        })
        .then(() => {
          deleteSavedUrlCoupon();
        })
        .catch((err) => {
          const message = parseCouponError(err, coupon);
          setCouponError(message);
          deleteSavedUrlCoupon();
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addCoupon, showCouponLine, coupon, cart]);

  // Show coupon errors if the cart explicitly has one
  useEffect(() => {
    if (cart?.couponErrorMessage) {
      setCouponError(cart.couponErrorMessage);
    } else if (couponError !== null) {
      // Clear any "stale" coupon errors if present
      setCouponError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart]);

  const handleCouponError = (message) => {
    setCouponError(message);
  };

  if (!cart) {
    return null;
  }

  const masterCart = cartPurchaseOrderSelector(cart);

  if (cart.contents?.length === 0 || !masterCart) {
    return (
      <div style={{ textAlign: 'center' }}>
        <strong>Your cart is empty</strong>
      </div>
    );
  }

  return (
    <>
      <CartItemList cart={cart} showSeasonalShipping={false} />
      <CartSummaryWrapper dataTestId="auth-fail-modal-cart-summary">
        <SubtotalLine subtotal={masterCart?.subtotal} />

        <ShippingLine />

        <TaxLine taxAmount={masterCart.taxAmount} />

        {/* Coupons need the child cart to add/remove coupons appropriately */}
        <CouponLine
          cart={cart}
          hideDeleteButton={!showCouponLine}
          onCouponRemove={onCouponRemove}
        />

        <StoreCreditLine
          availableStoreCredit={masterCart.availableStoreCredit}
          useableStoreCredit={masterCart.useableStoreCredit}
          storeCreditRedeemed={masterCart.storeCreditRedeemed}
        />

        {!masterCart.coupon && showCouponLine && (
          <>
            <div className={styles.couponFormContainer}>
              {/* Coupons need the child cart to add/remove coupons appropriately */}
              <CouponForm
                onCouponError={handleCouponError}
                onCouponAdd={onCouponAdd}
                cartUuid={cart.uuid}
              />

              {couponError && (
                <div className={styles.couponErrorMessage}>{couponError}</div>
              )}
            </div>
          </>
        )}

        <TotalLine totalPrice={masterCart?.totalPriceWithTax} />
      </CartSummaryWrapper>
    </>
  );
};

export default AuthFailModalCartSummary;
